import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { faGoogle, faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { useNavigate } from 'react-router-dom';
import api, { API_BASE } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './AuthModal.css';

type Mode = 'login' | 'signup' | 'forgotPassword';

type Props = {
  show: boolean;
  onHide: () => void;
  defaultMode?: Mode;
};

const AuthModal: React.FC<Props> = ({ show, onHide, defaultMode = 'login' }) => {
  const navigate = useNavigate();
  const { login, logout } = useAuth();

  const [mode, setMode] = useState<Mode>(defaultMode);
  const [forgotStep, setForgotStep] = useState<1 | 2>(1);

  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const [signupData, setSignupData] = useState({ fullName: '', username: '', email: '', phoneNumber: '', password: '', confirmPassword: '' });
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirmPassword, setShowSignupConfirmPassword] = useState(false);

  // Forgot Password States
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (!show) return;
    setMode(defaultMode);
    setForgotStep(1);
    setError('');
    setSuccess('');
    setValidated(false);
    setLoading(false);
  }, [show, defaultMode]);

  const modalTitle = useMemo(() => {
    if (mode === 'login') return 'Welcome Back';
    if (mode === 'signup') return 'Create Account';
    return 'Reset Password';
  }, [mode]);

  const closeAndReset = () => {
    setError('');
    setSuccess('');
    setValidated(false);
    setLoading(false);
    setForgotStep(1);
    onHide();
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await api.forgotPassword(forgotEmail);
      setSuccess('Verification code sent to your email.');
      setForgotStep(2);
    } catch (err: any) {
      setError(err?.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await api.resetPassword({ email: forgotEmail, otp: forgotOtp, newPassword });
      setSuccess('Password reset successful! You can now log in.');
      setMode('login');
      setForgotStep(1);
    } catch (err: any) {
      setError(err?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const afterAuthNavigate = () => {
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      if (userData?.role === 'admin') navigate('/admin/dashboard');
      else navigate('/user/home');
    } catch {
      navigate('/user/home');
    }
  };

  const handleSocialLogin = (provider: string) => {
    window.location.href = `${API_BASE}/auth/${provider}`;
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const ok = await login(loginUsername, loginPassword, 'student');
      if (!ok) {
        setError('Invalid username or password');
        return;
      }

      closeAndReset();
      afterAuthNavigate();
    } catch (err: any) {
      setError(typeof err?.message === 'string' ? err.message : 'Failed to log in');
    } finally {
      setLoading(false);
    }
  };

  const handleSignupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSignupData((p) => ({ ...p, [name]: value }));
  };

  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    setError('');
    setSuccess('');

    const fullName = signupData.fullName.trim();
    const username = signupData.username.trim();
    const email = signupData.email.trim();
    const phoneNumber = signupData.phoneNumber.trim();
    const password = signupData.password;
    const confirmPassword = signupData.confirmPassword;

    if (!fullName) {
      setError('Full name is required for certificate.');
      setValidated(true);
      return;
    }

    if (username.length < 3) {
      setError('Username must be at least 3 characters.');
      setValidated(true);
      return;
    }

    if (!email.includes('@')) {
      setError('Please provide a valid email address.');
      setValidated(true);
      return;
    }

    if (!phoneNumber) {
      setError('Mobile number is required.');
      setValidated(true);
      return;
    }

    if (!/^[+]?\d{7,20}$/.test(phoneNumber.replace(/[\s-]/g, ''))) {
      setError('Please provide a valid mobile number.');
      setValidated(true);
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      setValidated(true);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      setValidated(true);
      return;
    }

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    setLoading(true);
    try {
      await api.register({ fullName, username, email, phoneNumber, password });
      setSuccess('Account created. Please sign in.');
      setMode('login');
      setLoginUsername(username);
      setLoginPassword('');
    } catch (err: any) {
      const msg = typeof err?.message === 'string' ? err.message : '';
      if (msg.includes('User already exists') || msg.includes('409')) {
        setError('User with this username or email already exists.');
      } else if (msg.includes('email') || msg.includes('username') || msg.includes('password')) {
        setError('Please check your inputs: username (min 3), valid email, password (min 8).');
      } else {
        setError('Failed to create an account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={closeAndReset}
      centered
      dialogClassName={`auth-modal ${mode === 'login' ? 'auth-modal-login' : ''}`}
      contentClassName="auth-modal-content"
      backdropClassName="auth-modal-backdrop"
    >
      <Modal.Body className="p-0">
        <div className="auth-modal-shell">
          <div
            className="auth-modal-image"
            style={{
              backgroundImage: `url('/images/img1.jpg')`,
            }}
          />


          <div className={`auth-modal-right ${mode}`}>
            <div className="auth-modal-header">
              <div className="auth-modal-brand">
                <img src="/logo.png" alt="DataPylot" width={32} height={32} />
                <div className="auth-modal-brand-text">
                  <div className="auth-modal-brand-name">DataPylot</div>
                  <div className="auth-modal-brand-sub">Learn. Build. Grow.</div>
                </div>
              </div>

              {mode !== 'forgotPassword' && (
                <div className="auth-modal-toggle auth-modal-toggle-right">
                  <button
                    type="button"
                    className={`auth-toggle-btn ${mode === 'login' ? 'active' : ''}`}
                    onClick={() => {
                      setMode('login');
                      setError('');
                      setSuccess('');
                      setValidated(false);
                    }}
                  >
                    Login
                  </button>
                  <button
                    type="button"
                    className={`auth-toggle-btn ${mode === 'signup' ? 'active' : ''}`}
                    onClick={() => {
                      setMode('signup');
                      setError('');
                      setSuccess('');
                      setValidated(false);
                    }}
                  >
                    Sign Up
                  </button>
                </div>
              )}
            </div>

            <div className="auth-modal-title">
              <div className="auth-modal-title-main">{modalTitle}</div>
              <div className="auth-modal-title-sub">
                {mode === 'login' && 'Sign in to continue your learning journey.'}
                {mode === 'signup' && 'Create your account in less than a minute.'}
                {mode === 'forgotPassword' && (forgotStep === 1 ? 'Enter your email to receive a verification code.' : 'Enter the code and your new password.')}
              </div>
            </div>

            {error && <Alert variant="danger" className="auth-modal-alert">{error}</Alert>}
            {success && <Alert variant="success" className="auth-modal-alert">{success}</Alert>}

            {mode === 'login' && (
              <Form onSubmit={handleLoginSubmit} className="auth-modal-form">
                <Form.Group className="mb-3" controlId="auth-login-username">
                  <Form.Label className="fw-semibold small">Username or Email</Form.Label>
                  <Form.Control
                    type="text"
                    value={loginUsername}
                    onChange={(e) => setLoginUsername(e.target.value)}
                    placeholder="Enter your username"
                    required
                    className="auth-modal-input"
                  />
                </Form.Group>

                <Form.Group className="mb-2" controlId="auth-login-password">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="fw-semibold small mb-0">Password</Form.Label>
                    <button 
                      type="button" 
                      className="auth-modal-link p-0 small" 
                      style={{ fontSize: '12px' }}
                      onClick={() => setMode('forgotPassword')}
                    >
                      Forgot Password?
                    </button>
                  </div>
                  <div className="auth-modal-password">
                    <Form.Control
                      type={showLoginPassword ? 'text' : 'password'}
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      className="auth-modal-input pe-5"
                    />
                    <Button
                      variant="link"
                      type="button"
                      className="auth-modal-eye"
                      onClick={() => setShowLoginPassword((v) => !v)}
                      tabIndex={-1}
                    >
                      <FontAwesomeIcon icon={showLoginPassword ? faEyeSlash : faEye} />
                    </Button>
                  </div>
                </Form.Group>

                <Button type="submit" disabled={loading} className="auth-modal-submit mt-3">
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>

                <div className="auth-modal-foot">
                  <span>New here?</span>
                  <button type="button" className="auth-modal-link" onClick={() => setMode('signup')}>Create an account</button>
                </div>
              </Form>
            )}

            {mode === 'signup' && (
              <Form noValidate validated={validated} onSubmit={handleSignupSubmit} className="auth-modal-form">
                <Form.Group className="mb-2" controlId="auth-signup-fullName">
                  <Form.Label className="fw-semibold small">Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="fullName"
                    value={signupData.fullName}
                    onChange={handleSignupChange}
                    placeholder="Enter your full name"
                    required
                    className="auth-modal-input"
                  />
                  <div className="text-muted" style={{ fontSize: 11, marginTop: 4 }}>
                    This full name is needed for certificate
                  </div>
                  <Form.Control.Feedback type="invalid">Full name is required.</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="auth-signup-username">
                  <Form.Label className="fw-semibold small">Username</Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    value={signupData.username}
                    onChange={handleSignupChange}
                    placeholder="Choose a username"
                    required
                    minLength={3}
                    className="auth-modal-input"
                  />
                  <Form.Control.Feedback type="invalid">Username must be at least 3 characters.</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="auth-signup-email">
                  <Form.Label className="fw-semibold small">Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={signupData.email}
                    onChange={handleSignupChange}
                    placeholder="Enter your email"
                    required
                    className="auth-modal-input"
                  />
                  <Form.Control.Feedback type="invalid">Please provide a valid email address.</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="auth-signup-phoneNumber">
                  <Form.Label className="fw-semibold small">Mobile Number</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phoneNumber"
                    value={signupData.phoneNumber}
                    onChange={handleSignupChange}
                    placeholder="Enter your mobile number"
                    required
                    className="auth-modal-input"
                  />
                  <Form.Control.Feedback type="invalid">Mobile number is required.</Form.Control.Feedback>
                </Form.Group>

                <div className="auth-modal-grid">
                  <Form.Group className="mb-3" controlId="auth-signup-password">
                    <Form.Label className="fw-semibold small">Password</Form.Label>
                    <div className="auth-modal-password">
                      <Form.Control
                        type={showSignupPassword ? 'text' : 'password'}
                        name="password"
                        value={signupData.password}
                        onChange={handleSignupChange}
                        placeholder="Create password"
                        required
                        className="auth-modal-input pe-5"
                      />
                      <Button
                        variant="link"
                        type="button"
                        className="auth-modal-eye"
                        onClick={() => setShowSignupPassword((v) => !v)}
                        tabIndex={-1}
                      >
                        <FontAwesomeIcon icon={showSignupPassword ? faEyeSlash : faEye} />
                      </Button>
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="auth-signup-confirmPassword">
                    <Form.Label className="fw-semibold small">Confirm</Form.Label>
                    <div className="auth-modal-password">
                      <Form.Control
                        type={showSignupConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={signupData.confirmPassword}
                        onChange={handleSignupChange}
                        placeholder="Confirm"
                        required
                        className="auth-modal-input pe-5"
                        isInvalid={validated && signupData.password !== signupData.confirmPassword}
                      />
                      <Button
                        variant="link"
                        type="button"
                        className="auth-modal-eye"
                        onClick={() => setShowSignupConfirmPassword((v) => !v)}
                        tabIndex={-1}
                      >
                        <FontAwesomeIcon icon={showSignupConfirmPassword ? faEyeSlash : faEye} />
                      </Button>
                    </div>
                  </Form.Group>
                </div>

                <Button type="submit" disabled={loading} className="auth-modal-submit">
                  {loading ? 'Creating account...' : 'Create Account'}
                </Button>

                <div className="auth-modal-foot">
                  <span>Already have an account?</span>
                  <button type="button" className="auth-modal-link" onClick={() => setMode('login')}>Sign in</button>
                </div>
              </Form>
            )}

            {mode === 'forgotPassword' && (
              <div className="auth-modal-form">
                {forgotStep === 1 ? (
                  <Form onSubmit={handleForgotPassword}>
                    <Form.Group className="mb-4" controlId="auth-forgot-email">
                      <Form.Label className="fw-semibold small">Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="Enter your registered email"
                        required
                        className="auth-modal-input"
                      />
                    </Form.Group>
                    <Button type="submit" disabled={loading} className="auth-modal-submit">
                      {loading ? 'Sending code...' : 'Send Verification Code'}
                    </Button>
                  </Form>
                ) : (
                  <Form onSubmit={handleResetPassword}>
                    <Form.Group className="mb-3" controlId="auth-reset-otp">
                      <Form.Label className="fw-semibold small">Verification Code</Form.Label>
                      <Form.Control
                        type="text"
                        value={forgotOtp}
                        onChange={(e) => setForgotOtp(e.target.value)}
                        placeholder="Enter 6-digit OTP"
                        required
                        maxLength={6}
                        className="auth-modal-input text-center fw-bold"
                        style={{ letterSpacing: '8px', fontSize: '1.2rem' }}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="auth-reset-new-password">
                      <Form.Label className="fw-semibold small">New Password</Form.Label>
                      <div className="auth-modal-password">
                        <Form.Control
                          type={showNewPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="At least 8 characters"
                          required
                          className="auth-modal-input pe-5"
                        />
                        <Button
                          variant="link"
                          type="button"
                          className="auth-modal-eye"
                          onClick={() => setShowNewPassword((v) => !v)}
                          tabIndex={-1}
                        >
                          <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                        </Button>
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-4" controlId="auth-reset-confirm-password">
                      <Form.Label className="fw-semibold small">Confirm New Password</Form.Label>
                      <Form.Control
                        type="password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        placeholder="Re-type new password"
                        required
                        className="auth-modal-input"
                      />
                    </Form.Group>

                    <Button type="submit" disabled={loading} className="auth-modal-submit">
                      {loading ? 'Resetting password...' : 'Reset Password'}
                    </Button>
                  </Form>
                )}

                <div className="auth-modal-foot">
                  <button type="button" className="auth-modal-link" onClick={() => setMode('login')}>Back to login</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AuthModal;
